import React, { useEffect, useState } from 'react'
import { PageTitle } from '../../../../_metronic/layout/core'
import { CaptableManagementBreadCrumbs } from '../core/_constants'
import { useIntl } from 'react-intl'
import { InfoCard2 } from '../../widgets/components/UI/InfoCard2'
import '../styles/timeline.scss'
import { DisplayImage } from '../../widgets/components/General/DisplayImage'
import brandingConfig from '../../../../config/brandingConfig'
import { useAuth } from '../../auth'
import { getAllTimeline } from '../core/_requests'
import { DateTime } from 'luxon'
import { useCurrency } from '../../../hooks/useCurrency'
import { getCurrencySymbol } from '../../../core/_util'
import NoDataPage from '../../widgets/components/NoDataPage'
import TimelineSkeleton from '../components/TimelineSkeleton'

const Timeline = () => {
  const { companyId, companyCurrency } = useAuth()
  const { formatMessage } = useIntl()
  const [timelineLoading, setTimelineLoading] = useState(false)
  const [timelineData, setTimelineData] = useState<any[]>([])
  const { currencyOptions } = useCurrency()

  const getTimelines = async () => {
    setTimelineLoading(true)
    if (!companyId) return
    try {
      const {
        data: { data, success },
      } = await getAllTimeline(companyId)
      if (success) {
        setTimelineData(data)
      }
    } catch (error) {
      console.error('Error fetching convertible security classes:', error)
    } finally {
      setTimelineLoading(false)
    }
  }

  useEffect(() => {
    getTimelines()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <PageTitle
        breadcrumbs={CaptableManagementBreadCrumbs}
        description={formatMessage({ id: 'Timeline' })}
      >
        {formatMessage({ id: 'Cap Table Management' })}
      </PageTitle>
      <div className='px-6'>
        <div className='w-100'>
          <InfoCard2
            title={formatMessage({ id: 'How to Manage Your Cap Table Efficiently?' })}
            desc={formatMessage({
              id: `${brandingConfig.brandName}’s Cap Table Management feature offers a seamless and organized approach to track your equity, project future ownership scenarios, and analyze management events.`,
            })}
            slug={brandingConfig.helpLinks.captableManagementDashboardOverview}
            className='w-100 mb-0'
          />
        </div>
      </div>
      <div className='w-100 px-md-6'>
        {timelineLoading ?
          <TimelineSkeleton />
          :
          timelineData && timelineData.length === 0 ?
            <NoDataPage />
            :
            <div id='tracking'>
              <div className='tracking-list'>
                {timelineData &&
                  timelineData.length > 0 &&
                  timelineData.map((item, index) => {
                    let content
                    switch (item.eventType) {
                      case 'shareclasscreated':
                        content = formatMessage({
                          id: `${item.user.firstName} ${item.user.lastName} Created Share Class "${item.eventValue.className
                            }" and price per share set ${getCurrencySymbol(
                              companyCurrency,
                              currencyOptions
                            )}${item.eventValue.pricePerShare}`,
                        })
                        break

                      case 'sharesassigned':
                        content = formatMessage({
                          id: item.eventValue.companyNameOfAssignedTo
                            ? `${item.user.firstName} ${item.user.lastName} assigned ${item.eventValue.numberOfShares
                            } to ${item.eventValue.companyNameOfAssignedTo
                            } at the price of ${getCurrencySymbol(
                              companyCurrency,
                              currencyOptions
                            )}${item.eventValue.pricePerShare}`
                            : `${item.user.firstName} ${item.user.lastName} assigned ${item.eventValue.numberOfShares
                            } to ${item.eventValue.firstNameOfshareAssignedTo} ${item.eventValue.lastNameOfshareAssignedTo
                            } at the price of ${getCurrencySymbol(
                              companyCurrency,
                              currencyOptions
                            )}${item.eventValue.pricePerShare}`,
                        })
                        break

                      case 'scenariocreated':
                        content = formatMessage({
                          id: `${item.user.firstName} ${item.user.lastName} created "${item.eventValue.scenarioName}"`,
                        })
                        break
                      case 'scenariodeleted':
                        content = formatMessage({
                          id: `${item.user.firstName} ${item.user.lastName} deleted "${item.eventValue.scenarioName}"`,
                        })
                        break

                      default:
                        content = formatMessage({
                          id: 'Unknown event',
                        })
                        break
                    }

                    return (
                      <div
                        className={`tracking-item ${index === 0 ? 'pt-0' : ''} ${index === timelineData.length - 1 ? 'pb-0' : ''
                          }`}
                        key={item.captableTimelineId}
                      >
                        <div className='tracking-icon status-intransit'>
                          <DisplayImage
                            className='rounded-circle'
                            imgName={item?.user?.profileImage || ''}
                            alt={''}
                            height={35}
                            width={35}
                          />
                        </div>
                        <div className='tracking-date font-size-13 fw-semibold'>
                          <>
                            {DateTime.fromISO(item.createdAt, { zone: 'utc' }).toFormat(
                              'dd MMM yyyy'
                            )}
                            <span>
                              {DateTime.fromISO(item.createdAt, { zone: 'utc' }).toFormat('cccc')}
                            </span>
                          </>
                        </div>
                        <div className='bg-white p-4 rounded'>
                          {item.eventType === 'sharesassigned' && (
                            <div className='badge bg-success mb-2 text-white'>
                              {item.eventValue.className}
                            </div>
                          )}
                          <div className='tracking-content font-size-13 fw-semibold'>
                            {content}
                          </div>
                        </div>
                      </div>
                    )
                  })}
              </div>
            </div>
        }
      </div>
    </div>
  )
}

export default Timeline
